'use client'

import { useGyymApiGlobalErrorHandler } from '@gyym/sdk-react'
import { useScroll } from '@gyym/ui'
import { usePathname, useRouter } from 'next/navigation'
import React, { useEffect } from 'react'

import { GTMScript } from '@/components/Scripts/GoogleTagManager'
import { useAnalyticsTrackingEffect } from '@/hooks/useAnalytics'
import { identify, usePageView } from '@/lib/googleTagManager'
import { useAuthUser, useShouldChangePasswordEffect } from '@/modules/Auth'

import { Footer } from './Footer'
import { Header } from './Header'
import { SidebarContainer } from './SidebarContainer'
import { Container, Content } from './styles'

export { HEADER_HEIGHT } from './Header'

const STICKY_HEADER_OFFSET = 300

// これらのプレフィックス配下のLPは独自のヘッダー/フッターを持つ
// (/corp/about = 法人会員サービスLP。service.gyym.jp/10 からの移植)
const LP_PATH_PREFIXES = ['/awards/', '/lp/', '/corp/about']

export interface BaseLayoutProps {
  isStickyHeader?: boolean
  hideTabletFooter?: boolean
  children: React.ReactNode
}

export const BaseLayout = ({
  isStickyHeader = true,
  hideTabletFooter = false,
  children,
}: BaseLayoutProps) => {
  const pathname = usePathname()
  const isTop = pathname === '/'
  // LP pages provide their own header/footer (e.g. /awards/2025, /awards/2026, ...)
  const isLp = LP_PATH_PREFIXES.some((prefix) => pathname?.startsWith(prefix))
  const router = useRouter()
  const { isLoggedIn, authUser } = useAuthUser()
  const [isIdentifySubmitted, setIsIdentifySubmitted] = React.useState(false)

  usePageView()

  useEffect(() => {
    if (authUser && !isIdentifySubmitted) {
      setIsIdentifySubmitted(true)
      // Identify user
      identify(authUser)
    }
  }, [authUser, isIdentifySubmitted])

  useGyymApiGlobalErrorHandler(
    (e) => {
      if ((e.status === 401 || e.status === 419) && isLoggedIn) {
        // If the login state differs from server side, redirect to login page
        router.push('/login')
      } else {
        // Else reject the promise
        return Promise.reject(e)
      }
    },
    [isLoggedIn, router],
  )

  const [sidebarOpen, setSidebarOpen] = React.useState(false)
  const handleSidebarOpen = React.useCallback(() => setSidebarOpen(true), [])
  const handleSidebarClose = React.useCallback(() => setSidebarOpen(false), [])

  const { y } = useScroll()
  const isSticky = !isTop ? isStickyHeader : STICKY_HEADER_OFFSET < y

  // Show dialog if user should change password
  useShouldChangePasswordEffect()

  // Track analytics campaign
  useAnalyticsTrackingEffect()

  if (isLp) {
    return (
      <Container>
        <GTMScript />
        <Content topPadded={false}>{children}</Content>
      </Container>
    )
  }

  return (
    <Container>
      <GTMScript />

      <Header
        isSticky={isSticky}
        isTop={isTop}
        onMenuClick={handleSidebarOpen}
      />

      <Content
        topPadded={isSticky && !isTop} // Header height
      >
        {children}
      </Content>

      <SidebarContainer open={sidebarOpen} onClose={handleSidebarClose} />

      <Footer hideTabletFooter={hideTabletFooter} />
    </Container>
  )
}
